import styled, { css } from 'styled-components';
import {
  CARD_DESKTOP_ROUNDING,
  CARD_MIN_HEIGHT,
  OVERLAY_WIDTH_EVOLUTION,
} from '@/app/components/search/components/EvolutionCard/constants.ts';
import { loadAnimation } from '../../../styles/mixins';

import { imageWidthStyle } from '../utils/styled';

export const OVERLAY_CARD_IMAGE_HEIGHT = 168;

export const imageRoundingStyles = css<{ isOverlay?: boolean }>`
  ${({ isOverlay }) => {
    const ADJUSTED = `calc(${CARD_DESKTOP_ROUNDING} - 1px)`;
    if (isOverlay) {
      return css`
        border-radius: 16px 0 0 16px;

        @media (min-width: 768px) {
          border-radius: ${ADJUSTED} ${ADJUSTED} 0 0;
        }
      `;
    }

    return css`
      border-radius: ${ADJUSTED} 0 0 ${ADJUSTED};

      @media (max-width: 768px) {
        border-radius: ${ADJUSTED} ${ADJUSTED} 0 0;
      }
    `;
  }};
`;

const imageLoaderOverlayDesktopStyles = css`
  width: ${OVERLAY_WIDTH_EVOLUTION} !important;
  height: ${OVERLAY_CARD_IMAGE_HEIGHT}px !important;
  min-height: ${OVERLAY_CARD_IMAGE_HEIGHT}px !important;
`;

const imageLoaderOverlayMobileStyles = css`
  width: 104px;
  min-height: 110px;
`;

const imageLoaderMobileStyles = css`
  width: 100%;
  aspect-ratio: 2 / 1;
  min-height: 160px;
`;

export const ImageLoader = styled.div<{ isOverlay: boolean }>`
  width: 100%;
  min-height: 200px;
  position: relative;
  ${imageWidthStyle};
  ${loadAnimation};
  ${imageRoundingStyles};

  @media (max-width: 767px) {
    ${({ isOverlay }) => (isOverlay ? imageLoaderOverlayMobileStyles : imageLoaderMobileStyles)}
  }

  @media (min-width: 768px) {
    min-height: ${CARD_MIN_HEIGHT};
    flex-shrink: 0;
    ${({ isOverlay }) => isOverlay && imageLoaderOverlayDesktopStyles};
  }
`;

const desktopContainer = css`
  ${imageWidthStyle};
`;

const desktopContainerOverlay = css`
  @media (min-width: 768px) {
    width: ${OVERLAY_WIDTH_EVOLUTION};
    min-width: ${OVERLAY_WIDTH_EVOLUTION};
    height: ${OVERLAY_CARD_IMAGE_HEIGHT}px;
    max-height: 200px;
  }
`;

const mobileOverlay = css`
  width: 104px;
  height: 100%;
`;

export const showCarouselControlsOnHover = css`
  &:hover {
    .arrow-left,
    .arrow-right {
      opacity: 0.5;
    }

    .dots {
      opacity: 1;
    }
  }
`;

const noImageStyles = css`
  background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
`;

export const GalleryContainerOuter = styled.div`
  position: relative;
  ${showCarouselControlsOnHover};
`;

export const GalleryContainer = styled.div<{
  itemsLength: number;
  isOverlay: boolean;
  noImage: boolean;
  ref: (node: Element) => void;
}>`
  display: flex;
  flex: none;
  flex-flow: row nowrap;
  position: relative;
  width: 100%;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;
  height: 100%;
  ${imageRoundingStyles};

  ${({ noImage }) => noImage && noImageStyles};

  @media (max-width: 767px) {
    ${({ isOverlay }) => isOverlay && mobileOverlay};
  }

  @media (min-width: 768px) {
    ${({ isOverlay }) => (isOverlay ? desktopContainerOverlay : desktopContainer)};
  }

  ${({ itemsLength }) =>
    itemsLength > 1
      ? css`
          overflow: auto;
        `
      : css`
          overflow: hidden;
        `};

  &::-webkit-scrollbar {
    display: none;
  }

  &::after {
    content: '';
    padding-right: 0.02px;
  }
`;

export const CloseButtonContainer = styled.button<{ isDark: boolean; isSmall: boolean }>`
  position: absolute;
  top: 0;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  border: 0;
  left: 0;

  @media (min-width: 768px) {
    left: unset;
    right: 0;
  }

  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  transition: box-shadow 0.2s ease-in-out;
  user-select: none;

  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  margin: ${({ isSmall }) => (isSmall ? '8px' : '16px')};

  &:hover {
    box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.mediumDownShadow};
  }

  svg,
  img {
    width: 20px;
    height: 20px;
  }

  ${({ isDark }) =>
    isDark &&
    css`
      background-color: rgba(60, 60, 60, 0.5);

      &:hover {
        background-color: rgba(60, 60, 60, 0.8);
      }
    `}
`;
